import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { App } from 'antd';
import { scheduleAtomFamily, scheduleSavingAtomFamily } from '../atoms/settingAtoms';
import { updateSchedule } from '../api/callSettingMutations';
import { guardedClient } from '../../auth/api/clients';
import { extractProblemDetail } from '../../../shared/errors/problemDetail';
import { findInvalidScheduleEntries } from '../utils/findInvalidScheduleEntries';
import { findInvertedScheduleEntries } from '../utils/findInvertedScheduleEntries';
import { formatInvertedDaysMessage, formatMissingEdgesMessage } from '../utils/scheduleErrorMessages';
import { normalizeTimeInput } from '../utils/normalizeTimeInput';
import type { CallSchedule, Weekday } from '../types';

interface ScheduleEditing {
  /** 編集中のスケジュール。 */
  current: CallSchedule;
  /** 保存済みのスケジュール。current との差分が未保存の変更。 */
  baseline: CallSchedule;
  /** 保存リクエスト送信中フラグ。保存ボタンはローディング表示にすること。 */
  saving: boolean;
  changeTime: (day: Weekday, edge: 'from' | 'to', value: string) => void;
  toggleHoliday: (checked: boolean) => void;
  toggleNewYearHolidays: (checked: boolean) => void;
  /** 検証してバックエンドへ保存し、成功したら baseline を保存した内容に同期する。 */
  save: () => Promise<void>;
}

/** 広告コード設定1件分の架電スケジュールの参照と編集・保存操作を提供する。 */
export const useSchedule = (advertisementId: number): ScheduleEditing => {
  const { message } = App.useApp();
  const { current, baseline } = useAtomValue(scheduleAtomFamily(advertisementId));
  const saving = useAtomValue(scheduleSavingAtomFamily(advertisementId));
  const setState = useSetAtom(scheduleAtomFamily(advertisementId));

  const changeTime = useCallback(
    (day: Weekday, edge: 'from' | 'to', value: string) => {
      const next = normalizeTimeInput(value);
      setState(prev => ({
        ...prev,
        current: {
          ...prev.current,
          weekdays: {
            ...prev.current.weekdays,
            [day]: { ...prev.current.weekdays[day], [edge]: next },
          },
        },
      }));
    },
    [setState]
  );

  const toggleHoliday = useCallback(
    (checked: boolean) => {
      setState(prev => ({ ...prev, current: { ...prev.current, callOnHoliday: checked } }));
    },
    [setState]
  );

  const toggleNewYearHolidays = useCallback(
    (checked: boolean) => {
      setState(prev => ({ ...prev, current: { ...prev.current, callOnNewYearHolidays: checked } }));
    },
    [setState]
  );

  // useAtomCallback で安定参照のコールバックにし、保存時点の最新スケジュールと saving フラグを
  // get で同期的に読む（current を依存に取ると編集のたびにコールバックが再生成されるため）。
  // 内側の useCallback は useAtomCallback の useMemo（[callback] キー）を効かせるために必要。
  const save = useAtomCallback(
    useCallback(
      async (get, set) => {
        const savingAtom = scheduleSavingAtomFamily(advertisementId);
        // 再レンダリング（ボタンのローディング表示）前の連打による二重送信を防ぐ
        if (get(savingAtom)) return;
        const { current: schedule } = get(scheduleAtomFamily(advertisementId));
        const missing = findInvalidScheduleEntries(schedule.weekdays);
        if (missing.length > 0) {
          void message.error(formatMissingEdgesMessage(missing));
          return;
        }
        const inverted = findInvertedScheduleEntries(schedule.weekdays);
        if (inverted.length > 0) {
          void message.error(formatInvertedDaysMessage(inverted));
          return;
        }
        set(savingAtom, true);
        // catch 内の return に暗黙に頼らず成功を明示し、エラー後に baseline が同期される退行を防ぐ
        let succeeded = false;
        try {
          await updateSchedule(guardedClient, advertisementId, schedule);
          succeeded = true;
        } catch (error) {
          void message.error(extractProblemDetail(error) ?? '自動架電のスケジュールの保存に失敗しました');
        } finally {
          set(savingAtom, false);
        }
        if (!succeeded) return;
        // baseline には送信した schedule を使う。完了時点の current を使うと、
        // 保存中に行われた編集（未送信）まで保存済み扱いになり dirty 表示が偽る。
        set(scheduleAtomFamily(advertisementId), prev => ({ ...prev, baseline: schedule }));
        void message.success('自動架電のスケジュールを保存しました');
      },
      [advertisementId, message]
    )
  );

  return { current, baseline, saving, changeTime, toggleHoliday, toggleNewYearHolidays, save };
};
